import type { DraftJson } from '@apps/card/routes/CardCustomizer';
import { useCustomizationContext } from '@apps/card/routes/CardCustomizer';
import type { CardEnvelopeReturnAddressLayout, CardEnvelopeRecipientAddressLayout } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { CardDieCut } from '@apps/card/routes/CardCustomizer/components/CardDieCut';
import { CardPaperType } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useEventCallback } from 'yet-another-react-lightbox';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { AddAttributeFunction } from '../state/state.mapping';
import { getPossibleOptions } from './utils';
import { useGetFoilConfigs } from './useDeriveFoilIds';
import { findFoilLayer } from '@apps/card/utils';
import { isFoilTemplate } from '@apps/card/utils/isFoilTemplate';

const cardPaperTypeToOptionId = (paperType: CardPaperType): string => {
  switch (paperType) {
    case 'standard': {
      return 'signature';
    }
    default: {
      return paperType;
    }
  }
};

const optionIdToCardPaperType = (optionId: string): CardPaperType => {
  switch (optionId) {
    case 'signature': {
      return CardPaperType.signature;
    }
    default: {
      return optionId as CardPaperType;
    }
  }
};

export const useTaylorMappings = () => {
  const [_customizations, updateCustomizations] = useCustomizationContext(draft => draft.config.customizations);
  const [root, updateDraftRoot] = useCustomizationContext(draft => draft);
  const { getFoilConfigs } = useGetFoilConfigs();

  return useEventCallback((addAttribute: AddAttributeFunction, product: StationeryProduct) => {
    // Register possible attribute 'paperType'
    const paperTypeId = 'paperType';
    const paperTypeOptions = getPossibleOptions(product, paperTypeId);
    addAttribute(
      {
        productAttributeId: paperTypeId,
        getOptionId: (draftData: DraftJson) => {
          return cardPaperTypeToOptionId(draftData.customizations.paperType);
        },
        setOptionId: (newState: string) => {
          updateCustomizations(draftData => {
            draftData.paperType = optionIdToCardPaperType(newState);
          });
        },
        allowedOptionIDs: paperTypeOptions,
      },
      product,
    );

    const cardShapeId = 'shape';
    const cardShapeOptions = getPossibleOptions(product, cardShapeId);
    addAttribute(
      {
        productAttributeId: cardShapeId,
        getOptionId: (draftData: DraftJson) => {
          return draftData.customizations.shape;
        },
        setOptionId: (newState: string) => {
          updateCustomizations(draftData => {
            draftData.shape = newState as CardDieCut;
          });
        },
        allowedOptionIDs: cardShapeOptions,
      },
      product,
    );

    // Magnets mail in Taylor's A6 envelopes, which come in exactly three print
    // modes: blank, return address on the back flap, and return address on the
    // back flap plus a variable recipient front (Kaila, #joy-taylor 2026-10-07).
    // Recipient addressing is never offered without a return address, so the
    // two choices are coupled here: turning recipients on puts the return
    // address on the flap, and dropping the return address turns recipients
    // off. The product's attribute options already exclude a front return
    // address for magnets; this keeps the remaining pair consistent.
    const recipientsRequireReturnAddress = product.productType === 'magnet';

    const returnAddressId = 'returnAddress';
    const returnAddressOptions = getPossibleOptions(product, returnAddressId);
    addAttribute(
      {
        productAttributeId: returnAddressId,
        getOptionId: (draftData: DraftJson) => {
          return draftData.card.envelope.layout;
        },
        setOptionId: (newState: string) => {
          updateDraftRoot(draft => {
            draft.config.card.envelope.layout = newState as CardEnvelopeReturnAddressLayout;
            if (recipientsRequireReturnAddress && newState === 'none' && draft.config.card.envelope.recipientAddressLayout === 'front') {
              draft.config.card.envelope.recipientAddressLayout = 'none' as CardEnvelopeRecipientAddressLayout;
            }
          });
        },
        allowedOptionIDs: returnAddressOptions,
      },
      product,
    );

    const recipientAddressId = 'recipientAddress';
    const recipientAddressOptions = getPossibleOptions(product, recipientAddressId);
    addAttribute(
      {
        productAttributeId: recipientAddressId,
        getOptionId: (draftData: DraftJson) => {
          return draftData.card.envelope.recipientAddressLayout ?? 'none';
        },
        setOptionId: (newState: string) => {
          updateDraftRoot(draft => {
            draft.config.card.envelope.recipientAddressLayout = newState as CardEnvelopeRecipientAddressLayout;
            if (recipientsRequireReturnAddress && newState === 'front' && draft.config.card.envelope.layout === 'none') {
              draft.config.card.envelope.layout = 'back' as CardEnvelopeReturnAddressLayout;
            }
          });
        },
        allowedOptionIDs: recipientAddressOptions,
      },
      product,
    );

    const foilId = 'foil';
    // Foil colours are only offered on foil templates; every other template is pinned to 'none'.
    // A foil template never falls back to 'none', so its foil is always charged.
    const isFoil = isFoilTemplate(root.template);
    const foilOptions = getPossibleOptions(product, foilId).filter(optionId => (isFoil ? optionId !== 'none' : optionId === 'none'));
    const foilConfigs = getFoilConfigs(product, foilId);
    // Maps target OptionId to real ColorId on set
    // And stored ColorId to OptionId on get
    addAttribute(
      {
        productAttributeId: foilId,
        getOptionId: (draftData: DraftJson) => {
          if (!isFoil) {
            return 'none';
          }
          // Get colorId from draft
          const foilColorId = findFoilLayer(draftData.card.front.layers)?.foilData?.colorId;
          // Map colorId to optionId
          const foilOptionId = foilConfigs?.find(f => f.colorId === foilColorId)?.optionId;
          // Return optionId
          return foilOptionId ?? 'gold';
        },
        setOptionId: (newState: string) => {
          if (!isFoil) {
            return;
          }
          // Get colorId for matching option
          const foilColorId = foilConfigs?.find(f => f.optionId === newState)?.colorId;
          updateDraftRoot(draft => {
            const layer = findFoilLayer(draft.config.card.front.layers);
            if (layer?.foilData) {
              layer.foilData.colorId = foilColorId ?? 'GOLD';
            }
          });
        },
        allowedOptionIDs: foilOptions,
      },
      product,
    );

    addAttribute(
      {
        productAttributeId: 'cardBack',
        getOptionId: () => 'free',
        setOptionId: () => {},
        allowedOptionIDs: ['free'],
      },
      product,
    );
  });
};
